import { Widgets } from './Widget';

export interface WidgetOption {
    value: string
    label: string
}

export interface WidgetBuildContext {
    pushId(): string
    text(value: string, isPrimary?: boolean): string
    option(label: string): WidgetOption
}

export interface WidgetTableData {
    columns: string[]
    rows: number
}

export type WidgetFieldKind =
    | 'text'
    | 'rawText'
    | 'number'
    | 'boolean'
    | 'enum'
    | 'optionList'
    | 'media'
    | 'mediaList'
    | 'mediaName'
    | 'table'
    | 'ppe';

export interface WidgetFieldSpec {
    name: string
    kind: WidgetFieldKind
    /** Used when the field is not written. Text fields default to empty. */
    default: any
    /**
     * Shown by the debug buttons, which build an item with no fields at all.
     * A generated page never falls back to this: naming a real step
     * "Sample label" reads like a placeholder left behind by mistake.
     */
    sample?: any
    primary?: boolean
    writable?: boolean
    values?: string[]
    derivedFrom?: string
}

export interface WidgetCatalogField {
    name: string
    kind: WidgetFieldKind
    values?: string[]
    default: any
}

export interface WidgetCatalogEntry {
    type: string
    fields: WidgetCatalogField[]
}

export const SAMPLE_IMAGE_URL = 'assets/widgets/icon1.png';

export const SAMPLE_GALLERY_URLS = [
    'assets/widgets/icon1.png',
    'assets/widgets/icon2.png',
    'assets/widgets/icon3.png'
];

const POSITIONS = ['start', 'center', 'end'];

const optionalLabel = (sampleText: string): WidgetFieldSpec =>
    ({ name: 'label', kind: 'text', primary: true, default: '', sample: sampleText });
const required = (): WidgetFieldSpec => ({ name: 'required', kind: 'boolean', default: false });

export const EXTENSION_WIDGET_FIELDS: { [type: string]: WidgetFieldSpec[] } = {
    label: [
        optionalLabel('Sample label'),
        { name: 'vPosition', kind: 'enum', values: POSITIONS, default: 'center' },
        { name: 'hPosition', kind: 'enum', values: POSITIONS, default: 'center' }
    ],
    html: [
        { name: 'html', kind: 'text', primary: true, default: '', sample: '<p>Sample rich text</p>' }
    ],
    input: [
        optionalLabel('Sample input'),
        { name: 'placeholder', kind: 'text', default: '', sample: 'Type here' },
        required()
    ],
    number: [
        optionalLabel('Sample number'),
        { name: 'placeholder', kind: 'text', default: '', sample: '0' },
        { name: 'min', kind: 'number', default: null },
        { name: 'max', kind: 'number', default: null },
        { name: 'unit', kind: 'rawText', default: '' },
        { name: 'precision', kind: 'number', default: null },
        required()
    ],
    datepicker: [
        optionalLabel('Sample date'),
        { name: 'placeholder', kind: 'text', default: '' },
        { name: 'timepicker', kind: 'boolean', default: false },
        required()
    ],
    multicheckbox: [
        optionalLabel('Sample checkbox'),
        { name: 'options', kind: 'optionList', default: ['Option 1', 'Option 2'], sample: ['Option 1', 'Option 2', 'Option 3'] },
        required()
    ],
    radio: [
        optionalLabel('Sample radio'),
        { name: 'options', kind: 'optionList', default: ['Option 1', 'Option 2'], sample: ['Option 1', 'Option 2', 'Option 3'] },
        required()
    ],
    image: [
        optionalLabel('Sample image'),
        { name: 'imgUrl', kind: 'media', default: SAMPLE_IMAGE_URL },
        { name: 'imgName', kind: 'mediaName', derivedFrom: 'imgUrl', default: 'icon1.png' }
    ],
    gallery: [
        optionalLabel('Sample gallery'),
        { name: 'images', kind: 'mediaList', default: SAMPLE_GALLERY_URLS },
        { name: 'galleryType', kind: 'enum', values: ['grid'], default: 'grid', writable: false }
    ],
    video: [
        optionalLabel('Sample video'),
        { name: 'videoUrl', kind: 'media', default: '' },
        { name: 'videoName', kind: 'mediaName', derivedFrom: 'videoUrl', default: '' }
    ],
    pdf: [
        optionalLabel('Sample PDF'),
        { name: 'pdfUrl', kind: 'media', default: '' },
        { name: 'pdfName', kind: 'mediaName', derivedFrom: 'pdfUrl', default: '' }
    ],
    qr: [
        optionalLabel('Sample QR'),
        { name: 'qrfield', kind: 'rawText', default: '' },
        { name: 'qrValidation', kind: 'boolean', default: false },
        required()
    ],
    captureImage: [optionalLabel('Sample capture image'), required()],
    captureVideo: [optionalLabel('Sample capture video'), required()],
    signature: [optionalLabel('Sample signature'), required()],
    ppe: [
        optionalLabel('Sample PPE'),
        { name: 'ppeOptions', kind: 'ppe', values: ['face', 'head', 'hand'], default: 'head' },
        required()
    ],
    table: [
        { name: 'tableData', kind: 'table', default: { columns: ['Column 1', 'Column 2'], rows: 2 } },
        required()
    ],
    id: [
        optionalLabel('Sample ID'),
        required(),
        { name: 'useForFilter', kind: 'boolean', default: true }
    ]
};

export const EXTENSION_SUPPORTED_ITEM_TYPES = Object.keys(EXTENSION_WIDGET_FIELDS).filter(type => {
    const widget = Widgets[type];
    return !!widget && widget.showWidget !== false && !widget.comingSoon;
});

function fileNameFromUrl(url: string): string {
    if (typeof url !== 'string' || url.length === 0) {
        return '';
    }
    const withoutQuery = url.split('?')[0];
    const parts = withoutQuery.split('/');
    return parts[parts.length - 1];
}

function buildTableData(ctx: WidgetBuildContext, value: WidgetTableData) {
    const columns = Array.isArray(value?.columns) && value.columns.length > 0 ? value.columns : ['Column 1'];
    const rowCount = Number.isInteger(value?.rows) && value.rows > 0 ? value.rows : 1;
    return {
        columns: [{
            cells: columns.map((label, order) => ({
                key: ctx.pushId(),
                type: 'label',
                order,
                templateOptions: { label: ctx.text(label) }
            }))
        }],
        rows: Array.from({ length: rowCount }, () => ({
            cells: columns.map((label, order) => ({
                key: ctx.pushId(),
                type: 'empty',
                order,
                templateOptions: {}
            }))
        }))
    };
}

function coerceValue(spec: WidgetFieldSpec, provided: any, fields: any): any {
    switch (spec.kind) {
        case 'enum':
            return spec.values.includes(provided) ? provided : spec.default;
        case 'number':
            return typeof provided === 'number' && isFinite(provided) ? provided : spec.default;
        case 'boolean':
            return typeof provided === 'boolean' ? provided : spec.default;
        case 'optionList':
            return Array.isArray(provided) && provided.length > 0 ? provided : spec.default;
        case 'mediaList':
            return Array.isArray(provided) && provided.length > 0 ? provided : spec.default;
        case 'table':
            return provided && typeof provided === 'object' ? provided : spec.default;
        case 'text':
        case 'rawText':
        case 'media':
            return typeof provided === 'string' ? provided : spec.default;
        case 'mediaName':
            if (typeof provided === 'string' && provided.length > 0) {
                return provided;
            }
            if (fields && typeof fields[spec.derivedFrom] === 'string' && fields[spec.derivedFrom].length > 0) {
                return fileNameFromUrl(fields[spec.derivedFrom]);
            }
            return spec.default;
        default:
            return provided === undefined ? spec.default : provided;
    }
}

function writeField(templateOptions: any, spec: WidgetFieldSpec, value: any, ctx: WidgetBuildContext) {
    switch (spec.kind) {
        case 'text':
            templateOptions[spec.name] = ctx.text(value, spec.primary);
            return;
        case 'optionList':
            templateOptions[spec.name] = value.map(optionLabel => ctx.option(optionLabel));
            templateOptions['translationAvailable'] = true;
            return;
        case 'mediaList':
            templateOptions[spec.name] = value.map(url => ({ imgUrl: url, imgName: fileNameFromUrl(url), imgThumbnailUrl: url }));
            return;
        case 'ppe':
            templateOptions[spec.name] = { face: value === 'face', head: value === 'head', hand: value === 'hand' };
            return;
        case 'table':
            templateOptions[spec.name] = buildTableData(ctx, value);
            return;
        default:
            templateOptions[spec.name] = value;
    }
}

export function buildWidgetFromFields(
    type: string, fields: any, ctx: WidgetBuildContext, useSamples = false
) {
    const specs = EXTENSION_WIDGET_FIELDS[type];
    if (!specs) {
        return null;
    }
    const provided = fields && typeof fields === 'object' ? fields : {};
    const templateOptions = {};
    specs.forEach(spec => {
        const effective = useSamples && spec.sample !== undefined
            ? { ...spec, default: spec.sample }
            : spec;
        const value = effective.writable === false
            ? effective.default
            : coerceValue(effective, provided[spec.name], provided);
        writeField(templateOptions, effective, value, ctx);
    });
    return { key: ctx.pushId(), type, templateOptions };
}

/** The debug buttons: every field carries its sample, since none is supplied. */
export function buildSampleWidget(type: string, ctx: WidgetBuildContext) {
    return buildWidgetFromFields(type, {}, ctx, true);
}

export function getExtensionWidgetCatalog(): WidgetCatalogEntry[] {
    return EXTENSION_SUPPORTED_ITEM_TYPES.map(type => ({
        type,
        fields: EXTENSION_WIDGET_FIELDS[type]
            .filter(spec => spec.writable !== false && spec.kind !== 'mediaName')
            .map(spec => {
                const field: WidgetCatalogField = { name: spec.name, kind: spec.kind, default: spec.default };
                if (spec.values) {
                    field.values = spec.values;
                }
                return field;
            })
    }));
}
